import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import {
  Building2, Database, GraduationCap, Radio, LifeBuoy, BarChart3, FileText, BadgeCheck,
  Download, Loader2, RefreshCw, PlayCircle, ExternalLink, PlusCircle, CheckCircle2, XCircle,
} from "lucide-react";

const TABS = [
  { key: "pilots", label: "Ministry Pilots", icon: Building2 },
  { key: "migration", label: "Data Migration", icon: Database },
  { key: "training", label: "Training & Certification", icon: GraduationCap },
  { key: "readiness", label: "Go-Live Readiness", icon: Radio },
  { key: "support", label: "Operational Support", icon: LifeBuoy },
  { key: "analytics", label: "Performance Analytics", icon: BarChart3 },
  { key: "docs", label: "Deployment Documentation", icon: FileText },
  { key: "acceptance", label: "Acceptance Demonstration", icon: BadgeCheck },
];

const KPI = ({ label, value, hint, testid, tone }) => (
  <div className="cad-panel px-3 py-2" data-testid={testid}>
    <div className="font-mono-cad text-[9px] uppercase tracking-[0.22em] text-[#7C93A5]">{label}</div>
    <div className={"font-display font-bold mt-0.5 " + (tone || "text-[#F6F8FA]") + " text-[18px]"}>{value ?? "—"}</div>
    {hint && <div className="font-mono-cad text-[10px] text-[#B3C2CC] mt-0.5">{hint}</div>}
  </div>
);

export const NationalDeployment = () => {
  const [tab, setTab] = useState("pilots");
  return (
    <div className="p-6 space-y-4" data-testid="page-national-deployment">
      <div>
        <div className="font-mono-cad text-[10px] uppercase tracking-[0.28em] text-[#C99A2E]">
          Cadastre360 · Final Production Transition
        </div>
        <h1 className="font-display text-3xl font-bold text-[#F6F8FA] mt-1">National Deployment Readiness Centre</h1>
        <div className="text-[12.5px] text-[#B3C2CC] mt-1 max-w-4xl">
          Sovereign transition console for pilot management, national data migration, officer certification, go-live readiness,
          support operations, executive analytics, deployment documentation, and final acceptance demonstration.
        </div>
      </div>

      <div className="cad-panel px-2 py-2 flex flex-wrap gap-1 sticky top-2 z-10" data-testid="national-tabs">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.key;
          return (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={"national-tab-" + t.key}
                    className={"px-2.5 py-1.5 rounded-sm text-[11px] font-mono-cad uppercase tracking-[0.13em] inline-flex items-center gap-1.5 " +
                               (active ? "bg-[#145A7A]/60 text-[#F6F8FA]" : "text-[#B3C2CC] hover:bg-[#145A7A]/25")}>
              <Icon size={12} className="text-[#C99A2E]" /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "pilots" && <PilotsPanel />}
      {tab === "migration" && <MigrationPanel />}
      {tab === "training" && <TrainingPanel />}
      {tab === "readiness" && <ReadinessPanel />}
      {tab === "support" && <SupportPanel />}
      {tab === "analytics" && <AnalyticsPanel />}
      {tab === "docs" && <DocsPanel />}
      {tab === "acceptance" && <AcceptancePanel />}
    </div>
  );
};

// ============================================================================
const PilotsPanel = () => {
  const qc = useQueryClient();
  const list = useQuery({ queryKey: ["nat-pilots"], queryFn: async () => (await api.get("/cadastre360/national/pilots")).data });
  const [form, setForm] = useState({ ministry: "", province: "", district: "", administrator: "", target_go_live: "" });
  const create = useMutation({
    mutationFn: async () => (await api.post("/cadastre360/national/pilots", form)).data,
    onSuccess: () => { toast.success("Pilot registered"); qc.invalidateQueries({ queryKey: ["nat-pilots"] }); setForm({ ministry: "", province: "", district: "", administrator: "", target_go_live: "" }); },
    onError: (e) => toast.error(String(e?.response?.data?.detail || e.message)),
  });
  return (
    <div className="space-y-3" data-testid="panel-pilots">
      <div className="cad-panel p-4">
        <div className="font-mono-cad text-[10px] uppercase tracking-[0.22em] text-[#C99A2E]">Register a new pilot ministry</div>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-2 mt-3">
          {["ministry", "province", "district", "administrator", "target_go_live"].map((k) => (
            <input key={k} placeholder={k.replace(/_/g, " ")} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                   className="bg-[#071B2B] border cad-hairline rounded-sm px-2.5 py-1.5 text-[12px] text-[#F6F8FA] font-mono-cad"
                   data-testid={"pilot-input-" + k} />
          ))}
        </div>
        <button onClick={() => create.mutate()} disabled={!form.ministry || create.isPending}
                className="cad-btn-primary mt-3 px-3 py-1.5 text-[11px] rounded-sm inline-flex items-center gap-1"
                data-testid="pilot-create">
          {create.isPending ? <Loader2 size={11} className="animate-spin" /> : <PlusCircle size={11} />} Register pilot
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {(list.data || []).map((p) => (
          <div key={p.id} className="cad-panel p-4" data-testid={"pilot-card-" + p.id}>
            <div className="flex items-start justify-between">
              <div className="min-w-0">
                <div className="font-mono-cad text-[10px] uppercase tracking-[0.18em] text-[#C99A2E]">{p.province} · {p.district}</div>
                <div className="font-display font-bold text-[#F6F8FA] text-[14px] mt-0.5 truncate">{p.ministry}</div>
              </div>
              <span className={"cad-chip text-[9px] uppercase " + (p.status === "in_progress" ? "cad-chip-green" : (p.status === "training" ? "cad-chip-blue" : "cad-chip-gold"))}>
                {p.status}
              </span>
            </div>
            <div className="mt-2 text-[11px] font-mono-cad text-[#B3C2CC]">Admin: {p.administrator}</div>
            <div className="mt-1 text-[11px] text-[#DCE3E8]">{p.training_status}</div>
            <div className="text-[11px] text-[#DCE3E8]">{p.migration_status}</div>
            <div className="mt-2 grid grid-cols-2 gap-2">
              <div className="text-[10px] font-mono-cad text-[#7C93A5]">Progress <span className="text-[#C99A2E] text-[12px]">{p.progress_percent}%</span></div>
              <div className="text-[10px] font-mono-cad text-[#7C93A5]">Adoption <span className="text-[#C99A2E] text-[12px]">{p.user_adoption_percent}%</span></div>
              <div className="text-[10px] font-mono-cad text-[#7C93A5]">Kick-off <span className="text-[#F6F8FA] text-[11px]">{p.timeline?.kick_off}</span></div>
              <div className="text-[10px] font-mono-cad text-[#7C93A5]">Go-live <span className="text-[#F6F8FA] text-[11px]">{p.timeline?.target_go_live}</span></div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ============================================================================
const MigrationPanel = () => {
  const dash = useQuery({ queryKey: ["nat-mig-dash"], queryFn: async () => (await api.get("/cadastre360/national/migration/dashboard")).data });
  const imports = useQuery({ queryKey: ["nat-mig-list"], queryFn: async () => (await api.get("/cadastre360/national/migration/imports")).data });
  const qc = useQueryClient();
  const [form, setForm] = useState({ kind: "land_parcels", format: "excel", filename: "", total_records: 1000 });
  const create = useMutation({
    mutationFn: async () => (await api.post("/cadastre360/national/migration/import", form)).data,
    onSuccess: () => { toast.success("Import batch created"); qc.invalidateQueries({ queryKey: ["nat-mig-list"] }); qc.invalidateQueries({ queryKey: ["nat-mig-dash"] }); },
  });
  const retry = useMutation({
    mutationFn: async (id) => (await api.post(`/cadastre360/national/migration/import/${id}/retry`)).data,
    onSuccess: () => { toast.success("Retry scheduled"); qc.invalidateQueries({ queryKey: ["nat-mig-list"] }); },
  });
  const rb = useMutation({
    mutationFn: async (id) => (await api.post(`/cadastre360/national/migration/import/${id}/rollback`)).data,
    onSuccess: () => { toast.success("Batch rolled back"); qc.invalidateQueries({ queryKey: ["nat-mig-list"] }); qc.invalidateQueries({ queryKey: ["nat-mig-dash"] }); },
  });
  const d = dash.data || {};
  return (
    <div className="space-y-3" data-testid="panel-migration">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPI label="Total batches" value={d.total_batches} testid="mig-kpi-batches" />
        <KPI label="Records" value={(d.total_records || 0).toLocaleString()} testid="mig-kpi-records" />
        <KPI label="Success rate" value={(d.success_rate ?? 0) + "%"} testid="mig-kpi-rate" />
        <KPI label="Duplicates" value={(d.duplicate_records || 0).toLocaleString()} testid="mig-kpi-dupes" />
      </div>
      <div className="cad-panel p-4">
        <div className="font-mono-cad text-[10px] uppercase tracking-[0.22em] text-[#C99A2E]">New import batch</div>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-2 mt-3">
          <select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })}
                  className="bg-[#071B2B] border cad-hairline rounded-sm px-2.5 py-1.5 text-[12px] text-[#F6F8FA] font-mono-cad" data-testid="mig-kind">
            {(d.supported_kinds || []).map((x) => <option key={x} value={x}>{x}</option>)}
          </select>
          <select value={form.format} onChange={(e) => setForm({ ...form, format: e.target.value })}
                  className="bg-[#071B2B] border cad-hairline rounded-sm px-2.5 py-1.5 text-[12px] text-[#F6F8FA] font-mono-cad" data-testid="mig-format">
            {(d.supported_formats || []).map((x) => <option key={x} value={x}>{x}</option>)}
          </select>
          <input placeholder="filename" value={form.filename} onChange={(e) => setForm({ ...form, filename: e.target.value })}
                 className="bg-[#071B2B] border cad-hairline rounded-sm px-2.5 py-1.5 text-[12px] text-[#F6F8FA] font-mono-cad" data-testid="mig-file" />
          <input type="number" placeholder="records" value={form.total_records} onChange={(e) => setForm({ ...form, total_records: parseInt(e.target.value || "0", 10) })}
                 className="bg-[#071B2B] border cad-hairline rounded-sm px-2.5 py-1.5 text-[12px] text-[#F6F8FA] font-mono-cad" data-testid="mig-records" />
          <button onClick={() => create.mutate()} disabled={!form.filename}
                  className="cad-btn-primary px-3 py-1.5 text-[11px] rounded-sm inline-flex items-center gap-1"
                  data-testid="mig-create">
            {create.isPending ? <Loader2 size={11} className="animate-spin" /> : <PlusCircle size={11} />} Create batch
          </button>
        </div>
      </div>
      <div className="cad-panel p-1 overflow-x-auto">
        <table className="w-full text-[12px] min-w-[820px]">
          <thead><tr className="text-left border-b cad-hairline">
            {["Started", "Kind", "Format", "File", "Records", "Imported", "Errors", "AI quality", "Status", "Actions"].map((h) => (
              <th key={h} className="px-3 py-2 font-mono-cad text-[10px] uppercase tracking-[0.16em] text-[#7C93A5]">{h}</th>
            ))}
          </tr></thead>
          <tbody className="cad-tbody">
            {(imports.data || []).map((r) => (
              <tr key={r.id}>
                <td className="px-3 py-2 font-mono-cad text-[10px] text-[#B3C2CC]">{r.started_at?.slice(0, 19).replace("T", " ")}</td>
                <td className="px-3 py-2 font-mono-cad text-[11px] text-[#F6F8FA]">{r.kind}</td>
                <td className="px-3 py-2 font-mono-cad text-[11px] text-[#C99A2E]">{r.format}</td>
                <td className="px-3 py-2 text-[11px] text-[#DCE3E8] truncate max-w-[180px]">{r.filename}</td>
                <td className="px-3 py-2 font-mono-cad text-[11px]">{(r.total_records || 0).toLocaleString()}</td>
                <td className="px-3 py-2 font-mono-cad text-[11px] text-[#6BB88A]">{(r.imported_records || 0).toLocaleString()}</td>
                <td className="px-3 py-2 font-mono-cad text-[11px] text-[#E67E7E]">{(r.error_records || 0).toLocaleString()}</td>
                <td className="px-3 py-2 font-mono-cad text-[11px]">{r.ai_quality_score}%</td>
                <td className="px-3 py-2"><span className={"cad-chip text-[9px] uppercase " + (r.status === "completed" ? "cad-chip-green" : (r.status === "rolled_back" ? "cad-chip-red" : "cad-chip-blue"))}>{r.status}</span></td>
                <td className="px-3 py-2 space-x-1">
                  <button onClick={() => retry.mutate(r.id)} className="cad-btn-outline px-2 py-0.5 text-[10px] rounded-sm" data-testid={"mig-retry-" + r.id}>Retry</button>
                  <button onClick={() => rb.mutate(r.id)} className="cad-btn-outline px-2 py-0.5 text-[10px] rounded-sm" data-testid={"mig-rb-" + r.id}>Rollback</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ============================================================================
const TrainingPanel = () => {
  const qc = useQueryClient();
  const courses = useQuery({ queryKey: ["nat-courses"], queryFn: async () => (await api.get("/cadastre360/national/training/courses")).data });
  const progress = useQuery({ queryKey: ["nat-my-progress"], queryFn: async () => (await api.get("/cadastre360/national/training/officer-progress")).data });
  const dash = useQuery({ queryKey: ["nat-training-dash"], queryFn: async () => (await api.get("/cadastre360/national/training/dashboard")).data });
  const enrol = useMutation({
    mutationFn: async (course_id) => (await api.post("/cadastre360/national/training/enrol", { course_id })).data,
    onSuccess: () => { toast.success("Enrolled"); qc.invalidateQueries({ queryKey: ["nat-my-progress"] }); },
  });
  const complete = useMutation({
    mutationFn: async ({ course_id, score }) => (await api.post(`/cadastre360/national/training/complete/${course_id}?score=${score}`)).data,
    onSuccess: (d) => { toast[d.passed ? "success" : "warning"](d.passed ? ("Certified: " + d.certificate_id) : "Score below pass mark"); qc.invalidateQueries({ queryKey: ["nat-my-progress"] }); qc.invalidateQueries({ queryKey: ["nat-training-dash"] }); },
  });
  return (
    <div className="space-y-3" data-testid="panel-training">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPI label="Courses" value={dash.data?.courses} testid="trn-kpi-courses" />
        <KPI label="Enrolments" value={dash.data?.enrolments} testid="trn-kpi-enr" />
        <KPI label="Certificates issued" value={dash.data?.certificates_issued} testid="trn-kpi-certs" />
        <KPI label="My certificates" value={progress.data?.certified} testid="trn-kpi-mine" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {(courses.data || []).map((c) => {
          const myEnr = (progress.data?.enrollments || []).find((e) => e.course_id === c.id);
          const myCert = (progress.data?.certificates || []).find((x) => x.course_id === c.id);
          return (
            <div key={c.id} className="cad-panel p-4" data-testid={"course-" + c.id}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-mono-cad text-[10px] uppercase tracking-[0.18em] text-[#C99A2E]">{c.code} · {c.domain}</div>
                  <div className="font-display font-bold text-[#F6F8FA] text-[13px] mt-0.5">{c.title}</div>
                </div>
                <span className="cad-chip cad-chip-blue text-[9px] uppercase">{c.level}</span>
              </div>
              <div className="text-[11px] font-mono-cad text-[#B3C2CC] mt-2">{c.modules} modules · {c.duration_hours}h · {c.exam_marks} exam marks</div>
              {myCert && (
                <div className="mt-2 text-[11px] cad-chip cad-chip-green">Certified · {myCert.score}</div>
              )}
              <div className="mt-3 flex flex-wrap gap-1">
                {!myEnr && (
                  <button onClick={() => enrol.mutate(c.id)} className="cad-btn-primary px-2.5 py-1 text-[10px] rounded-sm" data-testid={"enrol-" + c.id}>Enrol</button>
                )}
                {myEnr && !myCert && (
                  <button onClick={() => complete.mutate({ course_id: c.id, score: 92 })}
                          className="cad-btn-outline px-2.5 py-1 text-[10px] rounded-sm" data-testid={"complete-" + c.id}>
                    Take assessment
                  </button>
                )}
                {myCert && (
                  <a href={API + "/cadastre360/national/training/verify/" + myCert.id} target="_blank" rel="noreferrer"
                     className="cad-btn-outline px-2.5 py-1 text-[10px] rounded-sm inline-flex items-center gap-1">
                    <ExternalLink size={10} /> Verify certificate
                  </a>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ============================================================================
const ReadinessPanel = () => {
  const r = useQuery({ queryKey: ["nat-readiness"], queryFn: async () => (await api.get("/cadastre360/national/go-live/readiness")).data, refetchInterval: 30000 });
  const d = r.data || {};
  return (
    <div className="space-y-3" data-testid="panel-readiness">
      <div className="cad-panel p-6 flex items-center gap-6">
        <div className="text-[64px] font-display font-bold text-[#C99A2E] leading-none" data-testid="readiness-percent">
          {d.overall_percent ?? "…"}%
        </div>
        <div>
          <div className="font-mono-cad text-[10px] uppercase tracking-[0.22em] text-[#C99A2E]">National go-live readiness</div>
          <div className="font-display text-2xl font-bold text-[#F6F8FA] mt-1">Verdict: {d.verdict || "…"}</div>
          <div className="text-[12px] text-[#B3C2CC] mt-1">
            {d.signed_evidence_packs ?? 0} signed evidence packs · {d.officers_certified ?? 0} officers certified ·
            provincial readiness {d.provincial_readiness_percent ?? 0}%
          </div>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {(d.components || []).map((c) => (
          <div key={c.key} className="cad-panel p-3" data-testid={"read-comp-" + c.key}>
            <div className="font-mono-cad text-[10px] uppercase text-[#7C93A5]">{c.label}</div>
            <div className="mt-1 text-[22px] font-display font-bold text-[#F6F8FA]">{c.score}%</div>
            <div className="w-full bg-[#071B2B] border cad-hairline h-1 mt-1">
              <div className={"h-1 " + (c.score >= 90 ? "bg-[#6BB88A]" : (c.score >= 75 ? "bg-[#C99A2E]" : "bg-[#E67E7E]"))} style={{ width: c.score + "%" }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ============================================================================
const SupportPanel = () => {
  const qc = useQueryClient();
  const dash = useQuery({ queryKey: ["nat-support-dash"], queryFn: async () => (await api.get("/cadastre360/national/support/dashboard")).data });
  const inc = useQuery({ queryKey: ["nat-incidents"], queryFn: async () => (await api.get("/cadastre360/national/support/incidents")).data });
  const kb = useQuery({ queryKey: ["nat-kb"], queryFn: async () => (await api.get("/cadastre360/national/support/knowledge-base")).data });
  const [form, setForm] = useState({ title: "", severity: "medium" });
  const create = useMutation({
    mutationFn: async () => (await api.post("/cadastre360/national/support/incidents", form)).data,
    onSuccess: () => { toast.success("Incident logged"); qc.invalidateQueries({ queryKey: ["nat-incidents"] }); qc.invalidateQueries({ queryKey: ["nat-support-dash"] }); setForm({ title: "", severity: "medium" }); },
  });
  const patch = useMutation({
    mutationFn: async ({ id, status }) => (await api.patch(`/cadastre360/national/support/incidents/${id}`, { status })).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["nat-incidents"] }); qc.invalidateQueries({ queryKey: ["nat-support-dash"] }); },
  });
  const d = dash.data || {};
  return (
    <div className="space-y-3" data-testid="panel-support">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPI label="Incidents total" value={d.total} testid="sup-kpi-total" />
        <KPI label="Open" value={d.open} testid="sup-kpi-open" />
        <KPI label="Critical" value={d.critical} testid="sup-kpi-crit" />
        <KPI label="Knowledge articles" value={(kb.data || []).length} testid="sup-kpi-kb" />
      </div>
      <div className="cad-panel p-4">
        <div className="font-mono-cad text-[10px] uppercase tracking-[0.22em] text-[#C99A2E]">Log new incident</div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-2 mt-3">
          <input placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                 className="bg-[#071B2B] border cad-hairline rounded-sm px-2.5 py-1.5 text-[12px] text-[#F6F8FA] font-mono-cad md:col-span-2" data-testid="sup-title" />
          <select value={form.severity} onChange={(e) => setForm({ ...form, severity: e.target.value })}
                  className="bg-[#071B2B] border cad-hairline rounded-sm px-2.5 py-1.5 text-[12px] text-[#F6F8FA] font-mono-cad" data-testid="sup-sev">
            {["low", "medium", "high", "critical"].map((x) => <option key={x} value={x}>{x}</option>)}
          </select>
          <button onClick={() => create.mutate()} disabled={!form.title}
                  className="cad-btn-primary px-3 py-1.5 text-[11px] rounded-sm inline-flex items-center gap-1" data-testid="sup-create">
            {create.isPending ? <Loader2 size={11} className="animate-spin" /> : <PlusCircle size={11} />} Log incident
          </button>
        </div>
      </div>
      <div className="cad-panel p-1 overflow-x-auto">
        <table className="w-full text-[12px] min-w-[720px]">
          <thead><tr className="text-left border-b cad-hairline">
            {["Code", "Title", "Severity", "Status", "Reporter", "SLA", "Created", "Actions"].map((h) => (
              <th key={h} className="px-3 py-2 font-mono-cad text-[10px] uppercase tracking-[0.16em] text-[#7C93A5]">{h}</th>
            ))}
          </tr></thead>
          <tbody className="cad-tbody">
            {(inc.data || []).map((r) => (
              <tr key={r.id}>
                <td className="px-3 py-2 font-mono-cad text-[11px] text-[#C99A2E]">{r.code}</td>
                <td className="px-3 py-2 text-[#F6F8FA]">{r.title}</td>
                <td className="px-3 py-2"><span className={"cad-chip text-[9px] uppercase " + (r.severity === "critical" ? "cad-chip-red" : (r.severity === "high" ? "cad-chip-gold" : "cad-chip-blue"))}>{r.severity}</span></td>
                <td className="px-3 py-2 font-mono-cad text-[11px]">{r.status}</td>
                <td className="px-3 py-2 font-mono-cad text-[10px] text-[#B3C2CC] truncate max-w-[180px]">{r.reporter}</td>
                <td className="px-3 py-2 font-mono-cad text-[11px] text-[#DCE3E8]">{r.sla_target_hours}h</td>
                <td className="px-3 py-2 font-mono-cad text-[10px] text-[#B3C2CC]">{r.created_at?.slice(0, 10)}</td>
                <td className="px-3 py-2">
                  <button onClick={() => patch.mutate({ id: r.id, status: "resolved" })}
                          className="cad-btn-outline px-2 py-0.5 text-[10px] rounded-sm" data-testid={"sup-resolve-" + r.id}>Resolve</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="cad-panel p-4">
        <div className="font-mono-cad text-[10px] uppercase tracking-[0.22em] text-[#C99A2E]">Knowledge base</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-3">
          {(kb.data || []).map((a) => (
            <div key={a.id} className="border-b cad-hairline pb-2">
              <div className="font-mono-cad text-[10px] uppercase text-[#7C93A5]">{a.category} · {a.id}</div>
              <div className="text-[13px] text-[#F6F8FA]">{a.title}</div>
              <div className="text-[11px] text-[#B3C2CC] mt-1">{a.body}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ============================================================================
const AnalyticsPanel = () => {
  const nat = useQuery({ queryKey: ["nat-analytics"], queryFn: async () => (await api.get("/cadastre360/national/analytics/national-overview")).data });
  const prov = useQuery({ queryKey: ["nat-provincial"], queryFn: async () => (await api.get("/cadastre360/national/analytics/provincial")).data });
  const off = useQuery({ queryKey: ["nat-officer"], queryFn: async () => (await api.get("/cadastre360/national/analytics/officer-productivity")).data });
  const d = nat.data || {};
  return (
    <div className="space-y-3" data-testid="panel-analytics">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPI label="Parcels" value={(d.totals?.parcels || 0).toLocaleString()} testid="an-kpi-parcels" />
        <KPI label="Mineral rights" value={(d.totals?.mineral_rights || 0).toLocaleString()} testid="an-kpi-rights" />
        <KPI label="Surveys" value={(d.totals?.surveys || 0).toLocaleString()} testid="an-kpi-surveys" />
        <KPI label="Avg land days" value={d.avg_processing_days?.land} testid="an-kpi-avg" />
      </div>
      <div className="cad-panel p-4">
        <div className="font-mono-cad text-[10px] uppercase tracking-[0.22em] text-[#C99A2E]">National KPIs</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-3">
          {(d.national_kpis || []).map((k, i) => (
            <div key={k.kpi + "-" + i} className="border-b cad-hairline pb-2 flex items-center gap-2">
              <div className="font-mono-cad text-[11px] text-[#F6F8FA] flex-1">{k.kpi}</div>
              <div className="font-mono-cad text-[13px] text-[#C99A2E]">{k.value}</div>
              <div className="font-mono-cad text-[10px] text-[#B3C2CC]">target {k.target}</div>
            </div>
          ))}
        </div>
        <div className="text-[11px] text-[#DCE3E8] font-mono-cad mt-3">AI trend: {d.ai_trend}</div>
      </div>
      <div className="cad-panel p-1 overflow-x-auto">
        <table className="w-full text-[12px] min-w-[640px]">
          <thead><tr className="text-left border-b cad-hairline">
            {["Province", "Registrations", "Avg days", "SLA %", "Officers", "Productivity"].map((h) => (
              <th key={h} className="px-3 py-2 font-mono-cad text-[10px] uppercase tracking-[0.16em] text-[#7C93A5]">{h}</th>
            ))}
          </tr></thead>
          <tbody className="cad-tbody">
            {(prov.data?.provinces || []).map((p) => (
              <tr key={p.province}>
                <td className="px-3 py-2 font-mono-cad text-[11px] text-[#F6F8FA]">{p.province}</td>
                <td className="px-3 py-2 font-mono-cad text-[11px]">{p.registrations.toLocaleString()}</td>
                <td className="px-3 py-2 font-mono-cad text-[11px]">{p.average_days}</td>
                <td className="px-3 py-2 font-mono-cad text-[11px] text-[#C99A2E]">{p.sla_percent}%</td>
                <td className="px-3 py-2 font-mono-cad text-[11px]">{p.officers}</td>
                <td className="px-3 py-2 font-mono-cad text-[11px]">{p.productivity_index}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="cad-panel p-1 overflow-x-auto">
        <table className="w-full text-[12px] min-w-[640px]">
          <thead><tr className="text-left border-b cad-hairline">
            {["Officer", "Cases closed", "Avg days", "SLA %", "Productivity"].map((h) => (
              <th key={h} className="px-3 py-2 font-mono-cad text-[10px] uppercase tracking-[0.16em] text-[#7C93A5]">{h}</th>
            ))}
          </tr></thead>
          <tbody className="cad-tbody">
            {(off.data?.officers || []).map((o, i) => (
              <tr key={o.officer + "-" + i}>
                <td className="px-3 py-2 font-mono-cad text-[11px] text-[#F6F8FA] truncate max-w-[260px]">{o.officer}</td>
                <td className="px-3 py-2 font-mono-cad text-[11px]">{o.cases_closed}</td>
                <td className="px-3 py-2 font-mono-cad text-[11px]">{o.average_days}</td>
                <td className="px-3 py-2 font-mono-cad text-[11px] text-[#C99A2E]">{o.sla_percent}%</td>
                <td className="px-3 py-2 font-mono-cad text-[11px]">{o.productivity_index}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ============================================================================
const DocsPanel = () => {
  const list = useQuery({ queryKey: ["nat-docs-library"], queryFn: async () => (await api.get("/cadastre360/national/docs/library")).data });
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="panel-docs">
      {(list.data?.documents || []).map((d) => (
        <div key={d.key} className="cad-panel p-4" data-testid={"doc-card-" + d.key}>
          <div className="font-mono-cad text-[10px] uppercase tracking-[0.18em] text-[#C99A2E]">{d.key}</div>
          <div className="font-display font-bold text-[#F6F8FA] text-[13px] mt-0.5">{d.title}</div>
          <div className="mt-3 flex flex-wrap gap-1">
            {["pdf", "docx", "html"].map((fmt) => (
              <a key={fmt} href={API + `/cadastre360/national/docs/${d.key}.${fmt}`} target="_blank" rel="noreferrer"
                 className="cad-btn-outline px-2.5 py-1 text-[10px] rounded-sm inline-flex items-center gap-1"
                 data-testid={"doc-dl-" + d.key + "-" + fmt}>
                <Download size={10} /> {fmt.toUpperCase()}
              </a>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

// ============================================================================
const AcceptancePanel = () => {
  const qc = useQueryClient();
  const reports = useQuery({ queryKey: ["nat-acc-reports"], queryFn: async () => (await api.get("/cadastre360/national/acceptance-demo/reports")).data });
  const run = useMutation({
    mutationFn: async () => (await api.post("/cadastre360/national/acceptance-demo/run", {})).data,
    onSuccess: (d) => { toast.success("Acceptance " + d.overall + " · " + d.passed + "/" + d.scope_size); qc.invalidateQueries({ queryKey: ["nat-acc-reports"] }); },
  });
  const latest = (reports.data || [])[0];
  return (
    <div className="space-y-3" data-testid="panel-acceptance">
      <div className="cad-panel p-4 flex flex-wrap items-center gap-3">
        <div className="text-[12px] text-[#B3C2CC] flex-1 min-w-[220px] leading-relaxed">
          Executive Acceptance Demonstration validates every module, workflow, API, role, report, GIS layer, migration function,
          certificate workflow, notification channel, executive dashboard, public portal, QR verification, export and audit function.
        </div>
        <button onClick={() => run.mutate()} disabled={run.isPending}
                className="cad-btn-primary px-3 py-1.5 text-[11px] rounded-sm inline-flex items-center gap-1" data-testid="acc-run">
          {run.isPending ? <Loader2 size={11} className="animate-spin" /> : <PlayCircle size={11} />} Run acceptance demo
        </button>
        <a href={API + "/cadastre360/national/acceptance-demo/certificate.pdf"} target="_blank" rel="noreferrer"
           className="cad-btn-outline px-3 py-1.5 text-[11px] rounded-sm inline-flex items-center gap-1"
           data-testid="acc-download-cert">
          <Download size={11} /> Executive Acceptance Certificate (PDF)
        </a>
      </div>
      {latest && (
        <div className="cad-panel p-4">
          <div className="flex items-center gap-3">
            <span className={"cad-chip text-[10px] uppercase " + (latest.overall === "PASS" ? "cad-chip-green" : "cad-chip-red")}>{latest.overall}</span>
            <span className="font-mono-cad text-[11px] text-[#F6F8FA]">{latest.id}</span>
            <span className="font-mono-cad text-[11px] text-[#B3C2CC]">{latest.passed}/{latest.scope_size} checks</span>
            <span className="font-mono-cad text-[10px] text-[#7C93A5] ml-auto">{latest.run_at?.slice(0, 19).replace("T", " ")}</span>
          </div>
          <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-2">
            {(latest.checks || []).map((c, i) => (
              <div key={c.name + "-" + i} className="border-b cad-hairline pb-1 flex items-start gap-2">
                {c.ok ? <CheckCircle2 size={12} className="text-[#6BB88A] mt-0.5" /> : <XCircle size={12} className="text-[#E67E7E] mt-0.5" />}
                <div>
                  <div className="text-[11px] text-[#F6F8FA]">{c.name}</div>
                  <div className="text-[10px] text-[#7C93A5] font-mono-cad">{c.detail}</div>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-3 text-[10px] font-mono-cad text-[#7C93A5] break-all">signature: {latest.signature}</div>
        </div>
      )}
    </div>
  );
};
